import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import * as BABYLONLOADERS from 'babylonjs-loaders';
import * as BABYLONEXPORT from 'babylonjs-serializers';
import Draco3d from 'draco3d';
import * as Inovx from '@shared';
import { getBabylonSceneSource } from './BabylonSceneLoader';
import { apiRequest } from '@shared/api/apiClient';
class GLBConverter {
    private _gltfLoader;
    constructor() {
        this._gltfLoader = BABYLONLOADERS;
    }

    public async compressGLBToBuffer(glbBuffer: ArrayBuffer): Promise<{ buffer: Buffer, boundingBox: BABYLON.BoundingBox }> {
        // Parse the GLB buffer
        const parsedGLB = this.parseGLB(glbBuffer);

        // Extract geometry data
        const geometryData = this.extractMeshGeometry(parsedGLB);

        // Compress meshes and rebuild the GLB
        const buffer = await this.compressMeshesAndRebuildGLB(parsedGLB, geometryData);

        // Calculate bounds by loading glb (as URL) into a scene, and checking extents
        const blob = new Blob([buffer], { type: "model/gltf-binary" });
        const objectUrl = URL.createObjectURL(blob);
        var boundingBox = await this.getGlbBounds(objectUrl, { X: 0, Y: 0, Z: 0 }, 1, 0);
        URL.revokeObjectURL(objectUrl);

        // Return the buffer and the bounding box
        return { buffer, boundingBox };
    }
    public async getGlbBounds(glbUrl: string, position : IVector3D, scale : number, rotation : number): Promise<BABYLON.BoundingBox> {
        // BABYLON scene loader needs a scene to load meshes.
        const canvas = document.createElement("canvas");
        const engine = new BABYLON.Engine(canvas, true);
        const dummyScene = new BABYLON.Scene(engine);

        const meshes = await this.loadGLBUrlIntoScene(dummyScene, "CompressedGLB", glbUrl, false);

        var rootTransform = meshes.find(mesh => mesh.name === "__root__");

        // Create a TransformNode as the root for positioning and scaling
        const transformParent = new BABYLON.TransformNode('', dummyScene);
        rootTransform.parent = transformParent;

        transformParent.position = new BABYLON.Vector3(position.X, position.Y, position.Z);
        transformParent.rotation = new BABYLON.Vector3(0, rotation, 0);
        transformParent.scaling = new BABYLON.Vector3(scale, scale, scale);

        var bounds = transformParent.getHierarchyBoundingVectors(true);

        dummyScene.dispose();
        engine.dispose();

        return new BABYLON.BoundingBox(bounds.min, bounds.max);
    }
    public async convertSceneToBuffer(scene: BABYLON.Scene, glbMeshFilter: string = ""): Promise<Buffer | undefined> {
        var that = this;
        let options = {
            shouldExportNode: function (node: BABYLON.TransformNode) {
                return true;
            },
        };

        var opt: BABYLONEXPORT.IExportOptions = {};

        opt.shouldExportNode =
            function (node: BABYLON.TransformNode) {

                return ((node.id == glbMeshFilter || glbMeshFilter == "") && node.isEnabled() && node.id != "AnchorSphere");
            };

        return BABYLONEXPORT.GLTF2Export.GLBAsync(scene as any, "GLB Scene", opt)
            .then(async (glbBuffer) => {
                try {
                    // Convert the Blob to DataView
                    const dataView = await that.convertBlobToDataView(glbBuffer.glTFFiles[Object.keys(glbBuffer.glTFFiles)[0]]);

                    // Parse the GLB file
                    const parsedGLB = that.parseGLB(dataView as ArrayBuffer);

                    // Extract geometry data
                    const geometryData = that.extractMeshGeometry(parsedGLB);

                    // Compress meshes and rebuild GLB
                    const compressedGLBBuffer = await that.compressMeshesAndRebuildGLB(parsedGLB, geometryData);

                    return compressedGLBBuffer;  // Return the compressed buffer
                } catch (error) {
                    console.error("Error during GLB compression:", error);
                    return undefined;  // Return undefined in case of an error
                }
            })
            .catch((error) => {
                console.error("Error exporting GLB:", error);
                return undefined;  // Return undefined in case of export error
            });
    }
    public async compressSceneToBuffer(scene: BABYLON.Scene, glbMeshFilter: string = ""): Promise<Buffer | undefined> {
        var that = this;
        let options = {
            shouldExportNode: function (node: BABYLON.TransformNode) {
                return true;
            },
        };

        var opt: BABYLONEXPORT.IExportOptions = {};

        opt.shouldExportNode =
            function (node: BABYLON.TransformNode) {

                return ((node.id == glbMeshFilter || glbMeshFilter == "") && node.isEnabled() && node.id != "AnchorSphere");
            };

        return BABYLONEXPORT.GLTF2Export.GLBAsync(scene as any, "GLB Scene", opt)
            .then(async (glbBuffer) => {
                try {
                    // Convert the Blob to DataView
                    const dataView = await that.convertBlobToDataView(glbBuffer.glTFFiles[Object.keys(glbBuffer.glTFFiles)[0]]);

                    // Parse the GLB file
                    const parsedGLB = that.parseGLB(dataView as ArrayBuffer);

                    // Extract geometry data
                    const geometryData = that.extractMeshGeometry(parsedGLB);

                    // Compress meshes and rebuild GLB
                    const compressedGLBBuffer = await that.compressMeshesAndRebuildGLB(parsedGLB, geometryData);

                    return compressedGLBBuffer;  // Return the compressed buffer
                } catch (error) {
                    console.error("Error during GLB compression:", error);
                    return undefined;  // Return undefined in case of an error
                }
            })
            .catch((error) => {
                console.error("Error exporting GLB:", error);
                return undefined;  // Return undefined in case of export error
            });
    }
    public async loadGLBFromURL(url: string): Promise<ArrayBuffer | undefined> {
        return apiRequest<ArrayBuffer>({
            url,
            method: 'GET',
            bodyType: 'none',
            responseType: 'arrayBuffer',
        })
            .then(glbBuffer => {
                return glbBuffer;  // Return the binary buffer
            })
            .catch(error => {
                console.error("Error loading GLB:", error);
                return undefined;  // Return undefined in case of an error
            });
    }

    public async loadGLBUrlIntoScene(scene: BABYLON.Scene, glbName: string, url: string, focusOnMeshes: boolean): Promise<BABYLON.AbstractMesh[]> {

        BABYLON.DracoCompression.Configuration = {
            decoder: {
                wasmUrl: "https://cdn.babylonjs.com/draco_wasm_wrapper_gltf.js",
                wasmBinaryUrl: "https://cdn.babylonjs.com/draco_decoder_gltf.wasm",
                fallbackUrl: "https://cdn.babylonjs.com/draco_decoder_gltf.js"
            }
        };

        const glbBuffer = await this.loadGLBFromURL(url);
        if (!glbBuffer) {
            return [];
        }

        const glbBlobUrl = URL.createObjectURL(
            new Blob([glbBuffer], { type: "model/gltf-binary" })
        );

        try {
            const sceneSource = getBabylonSceneSource(glbBlobUrl, ".glb");
            const result = await BABYLON.SceneLoader.ImportMeshAsync(null, sceneSource.rootUrl, sceneSource.sceneFilename, scene, null, sceneSource.pluginExtension);
            var validMeshes = result.meshes.filter(mesh => mesh !== null && mesh !== undefined && (mesh as BABYLON.Mesh).geometry != null);
            var comp = new BABYLON.DracoCompression();

            for (var x = 0; x < result.meshes.length; x++) {
                result.meshes[x].id = glbName;
            }
            for (var x = 0; x < result.transformNodes.length; x++) {
                result.transformNodes[x].id = glbName;
            }
            const meshes = result.meshes;

            if (validMeshes.length > 0 && focusOnMeshes) {
                this._focusCameraOnMeshes(scene, validMeshes as BABYLON.AbstractMesh[]);
            }

            return meshes;

        } catch (error) {

            console.error("Error loading GLB file 2:", error);
            return [];
        } finally {
            URL.revokeObjectURL(glbBlobUrl);
        }
    }
    public async loadGLBUrl( glbName: string, url: string): Promise<BABYLON.AbstractMesh[]> {

        BABYLON.DracoCompression.Configuration = {
            decoder: {
                wasmUrl: "https://cdn.babylonjs.com/draco_wasm_wrapper_gltf.js",
                wasmBinaryUrl: "https://cdn.babylonjs.com/draco_decoder_gltf.wasm",
                fallbackUrl: "https://cdn.babylonjs.com/draco_decoder_gltf.js"
            }
        };

        const glbBuffer = await this.loadGLBFromURL(url);
        if (!glbBuffer) {
            return [];
        }

        const glbBlobUrl = URL.createObjectURL(
            new Blob([glbBuffer], { type: "model/gltf-binary" })
        );

        try {
            const sceneSource = getBabylonSceneSource(glbBlobUrl, ".glb");
            const result = await BABYLON.SceneLoader.ImportMeshAsync(null, sceneSource.rootUrl, sceneSource.sceneFilename, null, null, sceneSource.pluginExtension);
            var validMeshes = result.meshes.filter(mesh => mesh !== null && mesh !== undefined && (mesh as BABYLON.Mesh).geometry != null);
            var comp = new BABYLON.DracoCompression();

            for (var x = 0; x < result.meshes.length; x++) {
                result.meshes[x].id = glbName;
            }
            for (var x = 0; x < result.transformNodes.length; x++) {
                result.transformNodes[x].id = glbName;
            }
            const meshes = result.meshes;

            return meshes;

        } catch (error) {

            console.error("Error loading GLB file 2:", error);
            return [];
        } finally {
            URL.revokeObjectURL(glbBlobUrl);
        }
    }

    private extractGeometryBounds(geometryData : any[]): BABYLON.BoundingBox
    {
        var minX = null;
        var minY = null;
        var minZ = null;

        var maxX = null;
        var maxY = null;
        var maxZ = null;

        for (var s = 0; s < geometryData.length; s++) {
            var positions = geometryData[s].positions as Float32Array;

            for (var p = 0; p < positions.length; p+=3) {
                var xVal = positions[p];
                var yVal = positions[p+1];
                var zVal = positions[p + 2];

                if (minX == null || minX > xVal)
                    minX = xVal
                if (minY == null || minY > yVal)
                    minY = yVal;
                if (minZ == null || minZ > zVal)
                    minZ = zVal

                if (maxX == null || maxX < xVal)
                    maxX = xVal
                if (maxY == null || maxY < yVal)
                    maxY = yVal
                if (maxZ == null || maxZ < zVal)
                    maxZ = zVal

            }
        }

        return new BABYLON.BoundingBox(new BABYLON.Vector3(minX, minY, minZ), new BABYLON.Vector3(maxX, maxY, maxZ));
    }

    private async compressMeshesAndRebuildGLB(parsedGLB: any, geometryData: any) : Promise<Buffer> {
        
        const compressedGeometries: any[] = new Array(geometryData.length);

        await Promise.all(
            geometryData.map(async (geometry: any, index: number) => {
                
                const retData = await this.compressMeshWithDraco(
                    geometry.geometryIndex,
                    geometry.positions,
                    geometry.indices,
                    geometry.normals,
                    geometry.colors,
                    geometry.uvs,
                    geometry.uv2s,
                    Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + '/lod/draco_encoder.wasm'
                );
                compressedGeometries[retData.index] = retData.compressedData;
                //compressedGeometries[retData.index] = [];
            })
        );
        
        //this.writeGLB(compressedGeometries[0]);
        return await this.rebuildGLB(parsedGLB, geometryData, compressedGeometries);
    }
    private async compressMeshWithDraco(
        index:any,
        positions: Float32Array,
        indices: Uint32Array,
        normals: Float32Array,
        colors: Float32Array,
        uvs: Float32Array,
        uv2s: Float32Array,
        wasmPath: string
    ) {
        //var wasmPath = Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + '/lod/draco_decoder.wasm';
        // Fetch and load the Draco WASM binary
        const wasmResponse = await fetch(wasmPath);
        if (!wasmResponse.ok) {
            throw new Error(`Failed to fetch WebAssembly binary: ${wasmResponse.statusText}`);
        }
        const wasmBinary = await wasmResponse.arrayBuffer();
        const dracoEncoder = await Draco3d.createEncoderModule({ wasmBinary });
        const encoder = new dracoEncoder.Encoder();

        // Set compression options
        encoder.SetAttributeQuantization(dracoEncoder.POSITION, 10); // Quantize position to 10 bits
        encoder.SetAttributeQuantization(dracoEncoder.NORMAL, 8);    // Quantize normals to 8 bits
        encoder.SetAttributeQuantization(dracoEncoder.TEX_COORD, 8); // Quantize UVs to 8 bits
        encoder.SetAttributeQuantization(dracoEncoder.COLOR, 8);

        // Create the Draco mesh and add attributes
        const meshBuilder = new dracoEncoder.MeshBuilder();
        const dracoMesh = new dracoEncoder.Mesh();

        // Add indices (faces)
        meshBuilder.AddFacesToMesh(dracoMesh, indices.length / 3, indices);

        // Add vertex positions (POSITION attribute)
        meshBuilder.AddFloatAttribute(dracoMesh, dracoEncoder.POSITION, positions.length / 3, 3, positions);

        // Add normals (if present, as NORMAL attribute)
        if (normals && normals.length > 0) {
            meshBuilder.AddFloatAttribute(dracoMesh, dracoEncoder.NORMAL, normals.length / 3, 3, normals);
        }

        // Add UVs (if present, as TEXCOORD_0 attribute)
        if (uvs && uvs.length > 0) {
            meshBuilder.AddFloatAttribute(dracoMesh, dracoEncoder.TEX_COORD, uvs.length / 2, 2, uvs);
        }

        // Add the second UV set (TEXCOORD_1) if it exists
        if (uv2s && uv2s.length > 0) {
            meshBuilder.AddFloatAttribute(dracoMesh, dracoEncoder.TEX_COORD, uv2s.length / 2, 2, uv2s); // For TEXCOORD_1
        }

        if (colors && colors.length > 0) {
            // Assuming color data is stored as RGBA or RGB values
            const colorComponentCount = (colors.length % 3 === 0) ? 3 : 4; // Check if RGB or RGBA
            meshBuilder.AddFloatAttribute(dracoMesh, dracoEncoder.COLOR, colors.length / colorComponentCount, colorComponentCount, colors);
        }

        // Encode the mesh with Draco
        const encodedData = new dracoEncoder.DracoInt8Array();
        const encodedLength = encoder.EncodeMeshToDracoBuffer(dracoMesh, encodedData);

        if (encodedLength <= 0) {
            throw new Error("Draco compression failed");
        }

        // Create the compressed data buffer
        const compressedData = new Uint8Array(encodedLength);
        for (let i = 0; i < encodedLength; i++) {
            compressedData[i] = encodedData.GetValue(i);
        }

        // Cleanup Draco resources
        dracoEncoder.destroy(encodedData);
        dracoEncoder.destroy(dracoMesh);
        dracoEncoder.destroy(encoder);
        dracoEncoder.destroy(meshBuilder);

        return { index: index, compressedData: compressedData };
    }
    
    private getAccessorData(parsedGLB: any, accessorIndex: any) {

        if (accessorIndex == null || accessorIndex == undefined)
            return null;

        const accessor = parsedGLB.gltf.accessors[accessorIndex];
        const bufferView = parsedGLB.gltf.bufferViews[accessor.bufferView];
        const binaryChunk = parsedGLB.binaryChunk; // The binary chunk is typically the first (or only) buffer

        return this.extractDataFromBuffer(binaryChunk, bufferView, accessor);
    }
    private extractDataFromBuffer(binaryChunk: any, bufferView: any, accessor: any) {
        // Get the buffer view byte offset and byte length
        const byteOffset = bufferView.byteOffset || 0;
        const byteLength = bufferView.byteLength;

        // Accessor details
        const componentType = accessor.componentType;
        const count = accessor.count;
        const type = accessor.type; // e.g., "VEC3" or "SCALAR"

        // Calculate the number of components per element (e.g., VEC3 has 3 components)
        const componentsPerType: any = {
            SCALAR: 1,
            VEC2: 2,
            VEC3: 3,
            VEC4: 4,
            MAT2: 4,
            MAT3: 9,
            MAT4: 16
        };

        const numComponents = componentsPerType[type];
        const TypedArray = this.getTypedArrayConstructor(componentType);

        // Calculate the total byte offset: bufferView's offset + accessor's offset (if any)
        const totalByteOffset = (accessor.byteOffset || 0) + byteOffset;

        // Calculate the number of bytes required for the data
        const totalExpectedByteLength = count * numComponents * TypedArray.BYTES_PER_ELEMENT;

        // Ensure the total length does not exceed the binary chunk's size
        if (totalByteOffset + totalExpectedByteLength > binaryChunk.byteLength) {
            console.error("Requested data exceeds binary chunk size.");
            return new TypedArray(0); // Return an empty array if there�s an issue
        }

        // Slice the binary chunk buffer to get just the relevant data for this buffer view
        const slicedBuffer = binaryChunk.slice(totalByteOffset, totalByteOffset + totalExpectedByteLength);

        // Extract data from the sliced buffer into the correct TypedArray
        const data = new TypedArray(slicedBuffer.buffer, slicedBuffer.byteOffset, slicedBuffer.byteLength / TypedArray.BYTES_PER_ELEMENT);

        return data;
    }

    private getTypedArrayConstructor(componentType:any) {
        switch (componentType) {
            case 5120: return Int8Array;     // BYTE
            case 5121: return Uint8Array;    // UNSIGNED_BYTE
            case 5122: return Int16Array;    // SHORT
            case 5123: return Uint16Array;   // UNSIGNED_SHORT
            case 5125: return Uint32Array;   // UNSIGNED_INT
            case 5126: return Float32Array;  // FLOAT
            default:
                throw new Error('Unknown component type: ' + componentType);
        }
    }
    private appendToBinaryChunk(dataArray: any, newBinaryChunk: Uint8Array, byteOffset: any) {
        const dataBuffer = new Uint8Array(dataArray);

        // Ensure byteOffset is 4-byte aligned before assigning to startByteOffset
        const startByteOffset = (byteOffset + 3) & ~3;

        // Check if the dataBuffer length is aligned to a 4-byte boundary
        const dataLength = dataBuffer.byteLength;
        const paddingLength = (4 - (dataLength % 4)) % 4; // Padding to make length a multiple of 4

        // Create the padding if needed
        const padding = new Uint8Array(paddingLength);

        // Combine the new dataBuffer with the existing binary chunk and padding
        const combinedChunk = new Uint8Array(newBinaryChunk.length + dataLength + paddingLength);

        // Copy the old binary chunk, data, and padding into the combined chunk
        combinedChunk.set(newBinaryChunk, 0);  // Copy existing data
        combinedChunk.set(dataBuffer, newBinaryChunk.length);  // Add new data
        if (paddingLength > 0) {
            combinedChunk.set(padding, newBinaryChunk.length + dataLength);  // Add padding
        }

        // Update the byteOffset to account for the new aligned position and added data length
        byteOffset = startByteOffset + dataLength + paddingLength;

        // Return the updated binary chunk and byte offset
        return { newBinaryChunk: combinedChunk, startByteOffset, length: dataLength + paddingLength, byteOffset };
    }
    
    private async rebuildGLB(parsedGLB: any, geometries: any, compressedGeometries: any) : Promise<Buffer> {
        // Step 1: Clear all existing bufferViews
        parsedGLB.gltf.bufferViews = [];

        // Rebuild bufferViews for compressed geometry
        let byteOffset = 0;
        //let bufferViewIndexMap = {}; // To track new bufferView indices for accessors
        let bufferViewIndexMap: { [key: number]: number } = {};
        // Ensure the KHR_draco_mesh_compression extension is listed in extensionsUsed/Required
        if (!parsedGLB.gltf.extensionsUsed) {
            parsedGLB.gltf.extensionsUsed = [];
        }
        if (!parsedGLB.gltf.extensionsUsed.includes('KHR_draco_mesh_compression')) {
            parsedGLB.gltf.extensionsUsed.push('KHR_draco_mesh_compression');
        }
        
        if (!parsedGLB.gltf.extensionsRequired) {
            parsedGLB.gltf.extensionsRequired = [];
        }
        if (!parsedGLB.gltf.extensionsRequired.includes('KHR_draco_mesh_compression')) {
            parsedGLB.gltf.extensionsRequired.push('KHR_draco_mesh_compression');
        }
        
        // Step 2: Rebuild bufferViews for compressed geometries
        let newBinaryChunk: Uint8Array = new Uint8Array(0);
        parsedGLB.gltf.buffers = [];

        for (let i = 0; i < geometries.length; i++) {

            const geometry = geometries[i];
            const compressedGeometry = compressedGeometries[i];

            // Append compressed geometry to the new binary chunk
            var start = newBinaryChunk.byteLength;
            var appendage = this.appendToBinaryChunk(compressedGeometry, newBinaryChunk, byteOffset);
            newBinaryChunk = appendage.newBinaryChunk;
            byteOffset += appendage.length;

            // Create new bufferView for this geometry
            const newBufferViewIndex = parsedGLB.gltf.bufferViews.length;
            parsedGLB.gltf.bufferViews.push({
                buffer: 0, // Refers to the first and only buffer (binary chunk)
                byteOffset: start,
                byteLength: appendage.length
            });

            // Store the bufferView index for future access
            bufferViewIndexMap[i] = newBufferViewIndex;

            // Update the primitive with the new bufferView and KHR_draco_mesh_compression extension
            const primitive = parsedGLB.gltf.meshes[geometry.meshIndex].primitives[geometry.primitiveIndex];

            primitive.extensions = {
                "KHR_draco_mesh_compression": {
                    "bufferView": i,
                    "attributes": {
                        "POSITION": geometry.positionAccessorIndex - geometry.positionAccessorIndex // Position attribute is always present
                    }
                }
            };

            // Add NORMAL attribute if it exists
            if (geometry.normals != null) {
                primitive.extensions["KHR_draco_mesh_compression"].attributes["NORMAL"] = geometry.normalAccessorIndex - geometry.positionAccessorIndex;
            }

            // Add TEXCOORD_0 attribute if it exists
            if (geometry.uvs != null) {
                primitive.extensions["KHR_draco_mesh_compression"].attributes["TEXCOORD_0"] = geometry.uvAccessorIndex - geometry.positionAccessorIndex;
            }

            if (geometry.uv2s != null) {
                primitive.extensions["KHR_draco_mesh_compression"].attributes["TEXCOORD_1"] = geometry.uv2AccessorIndex - geometry.positionAccessorIndex;
            }

            if (geometry.colors != null) {
                primitive.extensions["KHR_draco_mesh_compression"].attributes["COLOR_0"] = geometry.colorAccessorIndex - geometry.positionAccessorIndex;
            }
        }
            parsedGLB.gltf.asset = {
                version: "2.0",
                generator: "Custom Draco GLB Writer"
        };
             
        parsedGLB.gltf.buffers.push(
            {
                byteLength: newBinaryChunk.byteLength
            }
        );

        var gltf = parsedGLB.gltf;
        var compressedGeometryBuffer = newBinaryChunk;

        // 2. Convert GLTF JSON to a binary buffer (with padding to 4-byte alignment)
        const gltfJsonStr = JSON.stringify(gltf);
        const gltfJsonBuffer = Buffer.from(gltfJsonStr, 'utf8');
        const gltfJsonPaddedLength = Math.ceil(gltfJsonBuffer.length / 4) * 4; // Pad to 4-byte boundary
        const gltfJsonPadded = Buffer.alloc(gltfJsonPaddedLength, 0x20); // Padding with space (0x20) as per GLTF spec
        gltfJsonBuffer.copy(gltfJsonPadded);

        // 3. Create GLB header (12 bytes)
        const magic = Buffer.from("glTF", 'utf8'); // "glTF" in ASCII
        const version = Buffer.alloc(4);           // GLTF version 2
        version.writeUInt32LE(2, 0);
        const totalLength = Buffer.alloc(4);       // Total length of the GLB file

        // 4. Create GLB chunk headers
        const jsonChunkHeader = Buffer.alloc(8);   // 8 bytes: length + type (JSON)
        jsonChunkHeader.writeUInt32LE(gltfJsonPaddedLength, 0); // Length of JSON chunk
        jsonChunkHeader.writeUInt32LE(0x4E4F534A, 4);           // Chunk type: "JSON"

        const binChunkHeader = Buffer.alloc(8);    // 8 bytes: length + type (BIN)
        binChunkHeader.writeUInt32LE(compressedGeometryBuffer.byteLength, 0); // Length of binary chunk
        binChunkHeader.writeUInt32LE(0x004E4942, 4);                             // Chunk type: "BIN"

        // Calculate the padded length of the binary chunk
        const binLengthPadded = Math.ceil(compressedGeometryBuffer.byteLength / 4) * 4; // Ensure 4-byte alignment

        // Create padding for the binary data if necessary
        const binDataPadded = Buffer.concat([
            Buffer.from(compressedGeometryBuffer),
            Buffer.alloc(binLengthPadded - compressedGeometryBuffer.byteLength, 0x00) // Add padding if needed
        ]);

        // Now update the total length of the GLB, accounting for the padded binary data
        const totalGLBLength = 12 + jsonChunkHeader.length + gltfJsonPaddedLength + binChunkHeader.length + binDataPadded.length;
        totalLength.writeUInt32LE(totalGLBLength, 0);

        const glbBuffer = Buffer.concat([
            magic,          // "glTF" magic number
            version,        // GLTF version (2)
            totalLength,    // Total length of the GLB file
            jsonChunkHeader, // JSON chunk header
            gltfJsonPadded,  // Padded JSON chunk data
            binChunkHeader,  // Binary chunk header
            binDataPadded    // Padded binary chunk data
        ]);

        return glbBuffer;
    }
    
    private buildJsonChunk(gltfJson: any) {
        const jsonString = JSON.stringify(gltfJson);
        const jsonData = new TextEncoder().encode(jsonString);

        // Pad JSON chunk to 4-byte boundary
        const paddedLength = Math.ceil(jsonData.length / 4) * 4;
        const padding = paddedLength - jsonData.length;

        // Create a new buffer with the correct padded size
        const paddedJsonData = new Uint8Array(paddedLength);
        paddedJsonData.set(jsonData);

        // Append necessary padding bytes (0x20, or space character, is the default padding)
        for (let i = jsonData.length; i < paddedLength; i++) {
            paddedJsonData[i] = 0x20; // ASCII space
        }

        // JSON chunk header: chunk length and type ('JSON' = 0x4E4F534A)
        const chunkHeader = new Uint32Array([paddedJsonData.length, 0x4E4F534A]);

        return { chunkHeader, chunkData: paddedJsonData };
    }
    private buildGLB(jsonChunk: { chunkHeader: Uint32Array, chunkData: Uint8Array }, binaryChunk: Uint8Array) {
        // GLB Header (12 bytes)
        const header = new Uint32Array([0x46546C67, 2, 12 + jsonChunk.chunkData.byteLength + 8 + binaryChunk.byteLength]);

        // Create header for the binary chunk
        const binaryChunkHeader = new Uint32Array([binaryChunk.byteLength, 0x004E4942]); // "BIN" header

        // Calculate the total size for the new GLB file
        const totalLength = header.byteLength + jsonChunk.chunkHeader.byteLength + jsonChunk.chunkData.byteLength + binaryChunkHeader.byteLength + binaryChunk.byteLength;

        // Create the final buffer for the GLB file
        const glbBuffer = new ArrayBuffer(totalLength);
        const dataView = new Uint8Array(glbBuffer);

        let offset = 0;

        // Set the GLB header
        dataView.set(new Uint8Array(header.buffer), offset);
        offset += header.byteLength;

        // Set the JSON chunk (header and data)
        dataView.set(new Uint8Array(jsonChunk.chunkHeader.buffer), offset);
        offset += jsonChunk.chunkHeader.byteLength;
        dataView.set(new Uint8Array(jsonChunk.chunkData.buffer), offset);
        offset += jsonChunk.chunkData.byteLength;

        // Add the binary chunk (header and data)
        dataView.set(new Uint8Array(binaryChunkHeader.buffer), offset);
        offset += binaryChunkHeader.byteLength;
        dataView.set(new Uint8Array(binaryChunk.buffer), offset);

        return glbBuffer;
    }
    private extractMeshGeometry(parsedGLB: any) {
        const meshes = parsedGLB.gltf.meshes;

        var geometry: any[] = [];
        var geometryIndex = 0;
        // Loop over each mesh and its primitives
        for (var meshIndex = 0; meshIndex < meshes.length; meshIndex++)
        {
            var mesh = meshes[meshIndex];
            var primIndex = 0;

            mesh.primitives.forEach((primitive: any) => {
                const positionAccessorIndex = primitive.attributes.POSITION;
                const normalAccessorIndex = primitive.attributes.NORMAL;
                const indicesAccessorIndex = primitive.indices;
                const uvAccessorIndex = primitive.attributes.TEXCOORD_0;
                const uv2AccessorIndex = primitive.attributes.TEXCOORD_1;
                const colorAccessorIndex = primitive.attributes.COLOR_0;
                // Retrieve geometry data using accessors (positions, normals, indices, etc.)
                const positions = this.getAccessorData(parsedGLB, positionAccessorIndex);
                const normals = this.getAccessorData(parsedGLB, normalAccessorIndex);
                const indices = this.getAccessorData(parsedGLB, indicesAccessorIndex);
                const uvs = this.getAccessorData(parsedGLB, uvAccessorIndex);
                const uv2s = this.getAccessorData(parsedGLB, uv2AccessorIndex);
                const colors = this.getAccessorData(parsedGLB, colorAccessorIndex);
                // Add accessorIndex to geometry
                geometry.push({
                    positions: positions,
                    normals: normals,
                    indices: indices,
                    colors:colors,
                    uvs: uvs,
                    uv2s:uv2s,
                    positionAccessorIndex: positionAccessorIndex,
                    normalAccessorIndex: normalAccessorIndex,
                    indicesAccessorIndex: indicesAccessorIndex,
                    uvAccessorIndex: uvAccessorIndex,
                    uv2AccessorIndex: uv2AccessorIndex,
                    colorAccessorIndex:colorAccessorIndex,
                    meshIndex: meshIndex,
                    primitiveIndex: primIndex,
                    geometryIndex: geometryIndex
                });
                primIndex++;
                geometryIndex++;
            });
        }

        return geometry;
    }

    private convertBlobToDataView(blob: any) : Promise<ArrayBuffer | string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            // This function is triggered when the Blob has been read as an ArrayBuffer
            reader.onload = function (event) {
                const arrayBuffer = event.target.result as ArrayBuffer;
                //const dataView = new DataView(arrayBuffer);  // Wrap the ArrayBuffer in a DataView
                resolve(arrayBuffer);
            };

            reader.onerror = function () {
                reject(new Error("Error reading the Blob"));
            };

            // Read the Blob as an ArrayBuffer
            reader.readAsArrayBuffer(blob);
        });
    }
    private parseGLB(glbBuffer: ArrayBuffer) {
        // GLB Header is always 12 bytes: magic (4 bytes), version (4 bytes), length (4 bytes)
        const headerView = new DataView(glbBuffer, 0, 12);
        const magic = headerView.getUint32(0, true);  // Should be 0x46546C67 ('glTF' in ASCII)
        const version = headerView.getUint32(4, true);  // Usually 2 for glTF 2.0
        const length = headerView.getUint32(8, true);  // Total length of the GLB file

        // First chunk (JSON)
        const jsonChunkHeaderView = new DataView(glbBuffer, 12, 8);
        const jsonChunkLength = jsonChunkHeaderView.getUint32(0, true);
        const jsonChunkType = jsonChunkHeaderView.getUint32(4, true);  // Should be 0x4E4F534A ('JSON')
        const jsonChunk = new TextDecoder().decode(new Uint8Array(glbBuffer, 20, jsonChunkLength));
        const gltf = JSON.parse(jsonChunk);  // Parsed JSON chunk (scene graph, materials, etc.)

        // Second chunk (binary data)
        const binaryChunkHeaderView = new DataView(glbBuffer, 20 + jsonChunkLength, 8);
        const binaryChunkLength = binaryChunkHeaderView.getUint32(0, true);
        const binaryChunkType = binaryChunkHeaderView.getUint32(4, true);  // Should be 0x004E4942 ('BIN')
        const binaryChunk = new Uint8Array(glbBuffer, 20 + jsonChunkLength + 8, binaryChunkLength);

        return { gltf, binaryChunk };
    }

    // Utility function to adjust the camera
    private _focusCameraOnMeshes(scene: BABYLON.Scene, meshes: BABYLON.AbstractMesh[]) {
        const camera = scene.activeCamera as BABYLON.ArcRotateCamera;
        if (!camera) {
            console.warn("No active ArcRotateCamera found. Unable to adjust camera.");
            return;
        }

        // Initialize min and max vectors
        let min = new BABYLON.Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        let max = new BABYLON.Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

        // Loop through all meshes and expand the bounding box to fit them all
        meshes.forEach(mesh => {
            const boundingInfo = mesh.getBoundingInfo();
            const boundingBox = boundingInfo.boundingBox;

            // Update the min and max values based on each mesh's bounding box
            min = BABYLON.Vector3.Minimize(min, boundingBox.minimumWorld);
            max = BABYLON.Vector3.Maximize(max, boundingBox.maximumWorld);
        });

        // Calculate the center and the radius of the combined bounding box
        const center = BABYLON.Vector3.Center(min, max);
        const radius = BABYLON.Vector3.Distance(min, max) / 2;

        // Set the camera's target to the center of the bounding box
        camera.target = center;

        // Adjust the camera's radius to encompass the entire bounding box
        camera.radius = radius * 2.5;  // Adjust multiplier as needed to zoom in or out

        // Optionally adjust camera angles if necessary
        camera.alpha = Math.PI / 2;
        camera.beta = Math.PI / 4;
    }
}

export default GLBConverter;